import React, { useContext, useEffect, useState } from 'react';
import { Table, Tabs, Tab } from 'react-bootstrap';
import { CommonCollections } from '../../../../App';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { axiosGet } from '../../../../utils/axiosGet';
import { getNominationRoundYear } from '../../../../utils/getNominationRoundYear';

const RefereeHistory = () => {
  const { nominationId } = useContext(CommonCollections) || {};
  const [refereeFeedbackHistory, setRefereeFeedbackHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('feedback');

  useEffect(() => {
    const fetchRefereeFeedbackHistory = async () => {
      const response = await axiosGet(
        `referee-feedback-histories?filters[nominationId][$eq]=${nominationId}`,
      );

      if (response?.data?.data) {
        setRefereeFeedbackHistory(response.data.data);
      }
    };

    if (nominationId) {
      fetchRefereeFeedbackHistory();
    }
  }, [nominationId]);

  const { users, declineReasons, refereeStatuses, refereeReportArchivedArray } =
    useStaticCollections();

  const filteredRefereeReport = refereeReportArchivedArray.filter(
    (refereeReport) =>
      refereeReport.attributes.nominationId === nominationId &&
      refereeReport.attributes.independentAssessor === false,
  );

  const latestRoundYear = Math.max(
    ...filteredRefereeReport.map((report) => report.attributes.roundYear),
  );
  const latestRoundReports = filteredRefereeReport.filter(
    (refereeReport) => refereeReport.attributes.roundYear === latestRoundYear,
  );

  const refereeTable = latestRoundReports.map((refereeReport) => {
    const {
      extensionReason,
      declineReasonId,
      declineReasonOther,
      refereeStatusId,
      refereeUserId,
      extensionReasonApprovalNote,
      roundYear,
      updatedAt,
    } = refereeReport.attributes;
    refereeReport.refereeUserId = refereeUserId;

    const fullUserInfo = users[refereeUserId];
    refereeReport.refereeUser = fullUserInfo
      ? fullUserInfo.split('(')[0].trim()
      : 'Unknown User';

    refereeReport.refereeStatus =
      refereeStatusId === 1 ? 'No Response' : refereeStatuses[refereeStatusId];

    if (refereeStatusId === 3) {
      refereeReport.reason =
        declineReasonId === 5
          ? declineReasonOther
          : declineReasons[declineReasonId];
    } else if (refereeStatusId === 5) {
      refereeReport.reason = extensionReason;
    } else if (refereeStatusId === 6) {
      refereeReport.reason = extensionReasonApprovalNote;
    }
    refereeReport.round = roundYear;
    refereeReport.year = getNominationRoundYear(new Date(updatedAt));
    return refereeReport;
  });

  const parseFeedbackEntries = (feedbackData) => {
    const entries = [];

    feedbackData.forEach((feedbackEntry) => {
      if (feedbackEntry.attributes?.refereeFeedback) {
        feedbackEntry.attributes.refereeFeedback.forEach((feedback) => {
          const text = feedback.children
            ? feedback.children.map((child) => child.text).join(' ')
            : '';

          // Split by lines and process each line
          const lines = text.split('\n').filter((line) => line.trim());

          lines.forEach((line) => {
            // Pattern 1: "Name Declined Reason : reason Decline Year :year"
            let match = line.match(
              /^(.+?)\s+Declined\s+Reason\s*:\s*(.+?)\s+Decline\s+Year\s*:\s*(.+)$/i,
            );
            if (match) {
              const yearText = match[3].trim().replace(/^-\s*/, '');
              const yearMatch = yearText.match(/(\d{4})/);
              entries.push({
                name: match[1].trim().replace(/\s+/g, ' '),
                status: 'Declined',
                reason: match[2].trim(),
                year: yearMatch ? yearMatch[1] : yearText,
              });
              return;
            }

            // Pattern 2: "Name - Declined Reason : reason - year"
            match = line.match(
              /^(.+?)\s*-\s*Declined\s+Reason\s*:\s*(.+?)\s*-\s*(.+)$/i,
            );
            if (match) {
              entries.push({
                name: match[1].trim().replace(/\s+/g, ' '),
                status: 'Declined',
                reason: match[2].trim(),
                year: match[3].trim().replace(/^-\s*/, ''),
              });
              return;
            }

            // Pattern 3: "Name - No report provided for election round year"
            match = line.match(
              /^(.+?)\s*-\s*No report provided for election round\s*(.+)$/i,
            );
            if (match) {
              entries.push({
                name: match[1].trim().replace(/\s+/g, ' '),
                status: 'No Response',
                reason: '',
                year: match[2].trim().replace(/^-\s*/, ''),
              });
              return;
            }

            // Pattern 4: Generic "Name - Status/Reason - Year" or "Name - Status/Reason Year"
            match = line.match(/^(.+?)\s*-\s*(.+?)\s*-?\s*(\d{4})$/);
            if (match) {
              entries.push({
                name: match[1].trim().replace(/\s+/g, ' '),
                status: match[2].includes('Declined') ? 'Declined' : 'Other',
                reason: match[2].trim(),
                year: match[3].trim().replace(/^-\s*/, ''),
              });
              return;
            }

            // If no pattern matches but line has content, add as generic entry
            if (line.trim()) {
              const yearMatch = line.match(/(\d{4})/);
              const year = yearMatch ? yearMatch[1] : 'Unknown';

              entries.push({
                name: 'Unknown',
                status: 'Other',
                reason: line.trim(),
                year,
              });
            }
          });
        });
      }
    });

    return entries;
  };

  // Determine which tab to show by default based on available data
  useEffect(() => {
    if (refereeFeedbackHistory.length > 0 && refereeTable.length === 0) {
      setActiveTab('feedback');
    } else if (refereeFeedbackHistory.length === 0 && refereeTable.length > 0) {
      setActiveTab('reports');
    } else if (refereeFeedbackHistory.length > 0 && refereeTable.length > 0) {
      // Both have data - you could choose which to show first
      setActiveTab('feedback'); // or 'reports' depending on preference
    }
  }, [refereeFeedbackHistory.length, refereeTable.length]);

  const hasFeedbackHistory = refereeFeedbackHistory.length > 0;
  const hasRefereeReports = refereeTable.length > 0;

  return (
    nominationId &&
    (hasFeedbackHistory || hasRefereeReports) && (
      <>
        <div className="col-xl-12 col-lg-12 mt-xl-5 card-header">
          <h4>Referee History</h4>
        </div>

        <div className="col-xl-12 col-lg-12 mt-4">
          <Tabs
            activeKey={activeTab}
            onSelect={(tab) => setActiveTab(tab)}
            className="mb-3 custom-tabs"
          >
            {hasFeedbackHistory && (
              <Tab
                eventKey="feedback"
                title={`Feedback History (${parseFeedbackEntries(refereeFeedbackHistory).length})`}
              >
                <Table
                  bordered
                  striped
                  className="dataTable refereeFeedbackHistory mx-2"
                >
                  <thead className="thead-dark">
                    <tr>
                      <th>
                        <b>Name</b>
                      </th>
                      <th>
                        <b>Year</b>
                      </th>
                      <th>
                        <b>Status</b>
                      </th>
                      <th>
                        <b>Reason</b>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseFeedbackEntries(refereeFeedbackHistory).map(
                      (entry, index) => (
                        <tr key={index}>
                          <td>{entry.name}</td>
                          <td>{entry.year}</td>
                          <td>{entry.status}</td>
                          <td>{entry.reason}</td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </Table>
              </Tab>
            )}

            {hasRefereeReports && (
              <Tab
                eventKey="reports"
                title={`Round ${refereeTable.length > 0 ? refereeTable[0].year : ''} reports (${refereeTable.length})`}
              >
                <Table
                  bordered
                  striped
                  className="dataTable refereeHistory mx-2"
                >
                  <thead className="thead-dark">
                    <tr>
                      <th>
                        <b>Name</b>
                      </th>
                      <th>
                        <b>Year</b>
                      </th>
                      <th>
                        <b>Status</b>
                      </th>
                      <th>
                        <b>Reason</b>
                      </th>
                      <th>
                        <b>Round</b>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {refereeTable
                      .sort((a, b) =>
                        a.refereeUser.localeCompare(b.refereeUser),
                      )
                      .map((referee, index) => (
                        <tr key={index}>
                          <td>{referee.refereeUser}</td>
                          <td>{referee.year}</td>
                          <td>{referee.refereeStatus}</td>
                          <td>
                            {referee.refereeStatusId === 6 ? (
                              <>
                                <span>Reason: {referee.extensionReason}</span>
                                <br />
                                <span>
                                  Approval Note:{' '}
                                  {referee.extensionReasonApprovalNote}
                                </span>
                              </>
                            ) : (
                              <span>{referee.reason}</span>
                            )}
                          </td>
                          <td>{referee.round}</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </Tab>
            )}
          </Tabs>
        </div>
      </>
    )
  );
};

export default RefereeHistory;
